import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  BookOpen,
  Clock,
  Target,
  Calendar,
  ArrowRight,
  Play,
  FileText,
  Video,
  CheckCircle2,
} from "lucide-react";
import QuizModal from "@/components/QuizModal";
import { getVideoUrl, getPublicVideoUrl } from "@/lib/storageUrls";
import { useToast } from "@/hooks/use-toast";

export default function EmployeeDashboard() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [currentModule, setCurrentModule] = useState<any>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState<any>(null);
  const [isMCQOpen, setIsMCQOpen] = useState(false);
  const [mcqAnswers, setMcqAnswers] = useState<Record<string, number>>({});
  const [isShowcaseVideoOpen, setIsShowcaseVideoOpen] = useState(false);
  const [currentShowcaseVideo, setCurrentShowcaseVideo] = useState<
    string | null
  >(null);
  const [currentShowcaseVideoTitle, setCurrentShowcaseVideoTitle] = useState<
    string
  >("");
  const [showcaseVideoUrls, setShowcaseVideoUrls] = useState<{
    product: string | null;
    ceo: string | null;
    chro: string | null;
  }>({
    product: null,
    ceo: null,
    chro: null,
  });

  // Load video URLs for showcase videos
  useEffect(() => {
    const loadVideoUrls = async () => {
      try {
        const [productUrl, ceoUrl, chroUrl] = await Promise.all([
          getPublicVideoUrl("showcase-videos/servify-product-showcase.mp4").catch(() => null),
          getPublicVideoUrl("showcase-videos/ceo-message.mp4").catch(() => null),
          getPublicVideoUrl("showcase-videos/chro-message.mp4").catch(() => null),
        ]);

        setShowcaseVideoUrls({
          product: productUrl,
          ceo: ceoUrl,
          chro: chroUrl,
        });
      } catch (error) {
        console.error("Failed to load showcase video URLs:", error);
      }
    };

    loadVideoUrls();
  }, []);

  const { data: modulesData } = useQuery<{ modules: any[] }>({
    queryKey: ["/training-modules"],
  });

  const { data: progressData } = useQuery<{ progress: any[] }>({
    queryKey: ["/progress"],
  });

  const { data: quizzesData } = useQuery<{ quizzes: any[] }>({
    queryKey: ["/quizzes"],
  });

  const { data: quizAttemptsData } = useQuery<{ attempts: any[] }>({
    queryKey: ["/quiz-attempts"],
  });

  console.log("Modules data updated:", modulesData);
  console.log(
    "Available days:",
    modulesData?.modules
      .filter((m) => m.isActive)
      .map((m) => m.day)
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort((a, b) => a - b),
  );
  console.log("Progress data updated:", progressData);
  console.log("Quiz attempts data updated:", quizAttemptsData);
  console.log("Quizzes data:", quizzesData);

  const resetMCQAnswers = () => {
    setMcqAnswers({});
  };

  const handleMCQSubmit = (
    module: any,
    quizzesData: { quizzes: any[] } | undefined,
    onSuccess: () => void,
  ) => {
    if (!module || !quizzesData) return;

    const quiz = quizzesData.quizzes.find((q) => q.moduleId === module.id);
    if (!quiz) return;

    setCurrentQuiz(quiz);
    setIsQuizOpen(true);
    onSuccess();
  };

  const handleQuizComplete = () => {
    setIsQuizOpen(false);
    setCurrentQuiz(null);
  };

  // Handle video completion - check for linked quiz
  const handleVideoComplete = () => {
    console.log("Video completed for module:", currentModule);
    console.log("Quizzes data:", quizzesData);
    console.log("Current module ID:", currentModule?.id, "Type:", typeof currentModule?.id);
    
    if (currentModule) {
      // Check if this module has a linked quiz
      // Handle both string and number comparisons
      const moduleId = typeof currentModule.id === 'string' ? parseInt(currentModule.id) : currentModule.id;
      // Handle both array and object response formats
      const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
      console.log("Quizzes array:", quizzesArray);
      
      const quiz = quizzesArray.find((q: any) => {
        const quizModuleId = typeof q.moduleId === 'string' ? parseInt(q.moduleId) : q.moduleId;
        console.log(`Comparing moduleId: ${moduleId} (${typeof moduleId}) with quiz.moduleId: ${quizModuleId} (${typeof quizModuleId})`);
        return quizModuleId === moduleId;
      });
      
      console.log("Found quiz:", quiz);
      
      if (quiz) {
        console.log(`Module "${currentModule.title}" has a linked quiz, showing quiz modal...`);
        setIsVideoOpen(false);
        setCurrentQuiz(quiz);
        setIsQuizOpen(true);
        return;
      }

      // For Code of Conduct video, show MCQ after completion
      if (currentModule.title === "Code of Conduct") {
        console.log("Opening MCQ modal");
        setIsVideoOpen(false);
        setIsMCQOpen(true);
        return;
      }

      // For Refer a Buddy video, show quiz after completion
      if (currentModule.title === "Refer a Buddy") {
        console.log("Refer A Buddy video completed, opening quiz...");
        setIsVideoOpen(false);
        // Try to find quiz for this module
        const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
        const buddyQuiz = quizzesArray.find((q: any) => {
          const quizModuleId = typeof q.moduleId === 'string' ? parseInt(q.moduleId) : q.moduleId;
          const moduleId = typeof currentModule.id === 'string' ? parseInt(currentModule.id) : currentModule.id;
          return quizModuleId === moduleId;
        });
        if (buddyQuiz) {
          setCurrentQuiz(buddyQuiz);
          setIsQuizOpen(true);
        }
        return;
      }

      // No quiz linked, just close the video
      console.log(`No quiz found for "${currentModule.title}", closing video`);
      setIsVideoOpen(false);
      setCurrentModule(null);
    }
  };

  const modules = modulesData?.modules ?? [];
  const activeModules = modules.filter((m) => m.isActive);

  console.log(
    "Active modules only:",
    activeModules.map((m) => ({
      id: m.id,
      title: m.title,
      isActive: m.isActive,
    })),
  );

  const progress = progressData?.progress ?? [];
  const quizAttempts = quizAttemptsData?.attempts ?? [];

  const getModuleAvailability = (module: any) => {
    console.log(
      `Checking availability for module: ${module.title} (Day ${module.day}, Sequence ${module.sequence})`,
    );

    const activeModules = modules.filter((m) => m.isActive);
    const moduleProgress = progress.find((p) => p.moduleId === module.id);

    if (moduleProgress?.isCompleted) {
      console.log(
        `Module ${module.title} is already completed, allowing access`,
      );
      return true;
    }

    if (module.day === 1 && module.sequence === 1) {
      console.log(
        `Module ${module.title} is available (Day 1, Sequence 1 - first module)`,
      );
      return true;
    }

    const previousDayModules = activeModules
      .filter((m) => m.day < module.day)
      .sort((a, b) => b.day - a.day);

    if (previousDayModules.length > 0) {
      const lastPreviousDay = previousDayModules[0].day;
      const lastDayActiveModules = activeModules.filter(
        (m) => m.day === lastPreviousDay,
      );

      console.log(
        `Day ${module.day} - Previous day ${lastPreviousDay} active modules:`,
        lastDayActiveModules.map((m) => m.title),
      );

      const allPreviousDayCompleted = lastDayActiveModules.every((m) => {
        const p = progress.find((prog) => prog.moduleId === m.id);
        return p?.isCompleted === true;
      });

      console.log(
        `All previous day active modules completed: ${allPreviousDayCompleted}`,
      );

      if (!allPreviousDayCompleted) {
        return false;
      }
    }

    const sameDayPreviousModules = activeModules
      .filter((m) => m.day === module.day && m.sequence < module.sequence)
      .sort((a, b) => b.sequence - a.sequence);

    if (sameDayPreviousModules.length > 0) {
      const previousModule = sameDayPreviousModules[0];
      const previousProgress = progress.find(
        (p) => p.moduleId === previousModule.id,
      );
      const isAvailable = previousProgress?.isCompleted === true;

      console.log(
        `Previous active module in same day (${previousModule.title}) completed: ${isAvailable}`,
      );
      return isAvailable;
    }

    console.log(
      `No previous active module in same day found for ${module.title} - treating as available`,
    );
    return true;
  };

  const completedModules = activeModules.filter((module) => {
    const moduleProgress = progress.find((p) => p.moduleId === module.id);
    return moduleProgress?.isCompleted === true;
  }).length;

  const totalModules = activeModules.length;
  const overallProgress =
    totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

  console.log("Progress calculation based on active modules only:", {
    activeModuleIds: activeModules.map((m) => m.id),
    completedModulesOverall: completedModules,
    totalModules,
    overallProgress,
  });

  const totalTimeSpent = progress.reduce(
    (acc, p) => acc + (p.timeSpent || 0),
    0,
  );
  console.log("Total time spent from database:", totalTimeSpent, "seconds");

  const timeSpentMinutes = Math.floor(totalTimeSpent / 60);
  const timeSpentHours = Math.floor(timeSpentMinutes / 60);

  console.log("Time spent in minutes:", timeSpentMinutes);
  console.log("Time spent in hours:", timeSpentHours);

  console.log("Quiz attempts data:", quizAttemptsData);
  console.log("Quizzes data:", quizzesData);

  const quizAverage =
    quizAttempts.length > 0
      ? Math.round(
          quizAttempts.reduce((acc, attempt) => acc + (attempt.score || 0), 0) /
            quizAttempts.length,
        )
      : 0;

  console.log("Calculated quiz average:", quizAverage);
  console.log("Quiz attempts count:", quizAttempts.length);

  const { data: userData } = useQuery<any>({
    queryKey: ["/auth/me"],
  });

  const daysLeft = userData?.user?.dateOfJoining
    ? Math.max(
        0,
        7 -
          Math.floor(
            (new Date().getTime() -
              new Date(userData.user.dateOfJoining).getTime()) /
              (1000 * 60 * 60 * 24),
          ),
      )
    : 0;

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <div>
        <h1 className="text-3xl font-bold text-servify-dark-grey mb-2">
          Welcome back, {userData?.user?.firstName || "Employee"}!
        </h1>
        <p className="text-servify-dark-grey/70">
          Here's your onboarding progress overview
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Overall Progress */}
        <Card className="border-servify-white-grey shadow-lg hover:shadow-xl transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-servify-purple/10 rounded-lg">
                <Target className="h-6 w-6 text-servify-purple" />
              </div>
              <span className="text-2xl font-bold text-servify-purple">
                {overallProgress}%
              </span>
            </div>
            <h3 className="text-sm font-medium text-servify-dark-grey/70 mb-2">
              Overall Progress
            </h3>
            <Progress value={overallProgress} className="h-2" />
            <p className="text-xs text-servify-dark-grey/60 mt-2">
              {completedModules} of {totalModules} modules completed
            </p>
          </CardContent>
        </Card>

        {/* Time Spent */}
        <Card className="border-servify-white-grey shadow-lg hover:shadow-xl transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-servify-green/10 rounded-lg">
                <Clock className="h-6 w-6 text-servify-green" />
              </div>
              <span className="text-2xl font-bold text-servify-green">
                {timeSpentHours > 0
                  ? `${timeSpentHours}h`
                  : `${timeSpentMinutes}m`}
              </span>
            </div>
            <h3 className="text-sm font-medium text-servify-dark-grey/70">
              Time Spent Learning
            </h3>
            <p className="text-xs text-servify-dark-grey/60 mt-2">
              Total time on training modules
            </p>
          </CardContent>
        </Card>

        {/* Quiz Average */}
        <Card className="border-servify-white-grey shadow-lg hover:shadow-xl transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <BookOpen className="h-6 w-6 text-blue-500" />
              </div>
              <span className="text-2xl font-bold text-blue-500">
                {quizAverage}%
              </span>
            </div>
            <h3 className="text-sm font-medium text-servify-dark-grey/70">
              Quiz Average
            </h3>
            <p className="text-xs text-servify-dark-grey/60 mt-2">
              {quizAttempts.length}{" "}
              {quizAttempts.length === 1 ? "quiz" : "quizzes"} completed
            </p>
          </CardContent>
        </Card>

        {/* Days Left */}
        <Card className="border-servify-white-grey shadow-lg hover:shadow-xl transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <Calendar className="h-6 w-6 text-orange-500" />
              </div>
              <span className="text-2xl font-bold text-orange-500">
                {daysLeft}
              </span>
            </div>
            <h3 className="text-sm font-medium text-servify-dark-grey/70">
              Days Left
            </h3>
            <p className="text-xs text-servify-dark-grey/60 mt-2">
              To complete onboarding
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Access Card */}
      <Card className="bg-gradient-to-r from-servify-purple to-servify-purple/80 border-none shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-white mb-2">
                Let's get started!
              </h3>
              <p className="text-white/90 text-sm">
                Visit the Knowledge Hub to dive into Servify and complete your
                modules.
              </p>
            </div>
            <Button
              onClick={() => setLocation("/knowledge-hub")}
              className="bg-white text-servify-purple hover:bg-gray-100 font-semibold px-6 py-3"
              data-testid="button-knowledge-hub-quick-access"
            >
              Go to Knowledge Hub
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Video Showcase Section */}
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-servify-dark-grey">
            Discover Servify
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Video 1 - Product Showcase */}
          <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="bg-servify-light-grey rounded-lg overflow-hidden mb-4">
                {showcaseVideoUrls.product ? (
                  <video 
                    controls
                    className="w-full h-40 object-cover rounded-lg"
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      // Disable picture-in-picture
                      const video = e.currentTarget;
                      video.disablePictureInPicture = true;
                    }}
                    onContextMenu={(e) => {
                      // Prevent right-click context menu that can trigger PiP
                      e.preventDefault();
                    }}
                    disablePictureInPicture={true}
                  >
                    <source src={showcaseVideoUrls.product} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-servify-purple/10 to-servify-purple/5 flex items-center justify-center rounded-lg">
                    <p className="text-servify-dark-grey/50">Loading video...</p>
                  </div>
                )}
              </div>
              <h3 className="font-semibold text-servify-dark-grey mb-2">
                Welcome to Servify
              </h3>
              <p className="text-sm text-servify-dark-grey/70">
                Discover Servify's innovative device care solutions
              </p>
            </CardContent>
          </Card>

          {/* Video 2 - CEO Message */}
          <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="bg-servify-light-grey rounded-lg overflow-hidden mb-4">
                {showcaseVideoUrls.ceo ? (
                  <video 
                    controls
                    className="w-full h-40 object-cover rounded-lg"
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      // Disable picture-in-picture
                      const video = e.currentTarget;
                      video.disablePictureInPicture = true;
                    }}
                    onContextMenu={(e) => {
                      // Prevent right-click context menu that can trigger PiP
                      e.preventDefault();
                    }}
                    disablePictureInPicture={true}
                  >
                    <source src={showcaseVideoUrls.ceo} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-servify-purple/10 to-servify-purple/5 flex items-center justify-center rounded-lg">
                    <p className="text-servify-dark-grey/50">Loading video...</p>
                  </div>
                )}
              </div>
              <h3 className="font-semibold text-servify-dark-grey mb-2">
                Vision in Action at Servify
              </h3>
              <p className="text-sm text-servify-dark-grey/70">
                Driving Innovation and Growth – A Message from our Founder &
                CEO, Sreevathsa Prabhaker
              </p>
            </CardContent>
          </Card>

          {/* Video 3 - CHRO Message */}
          <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="bg-servify-light-grey rounded-lg overflow-hidden mb-4">
                {showcaseVideoUrls.chro ? (
                  <video 
                    controls
                    className="w-full h-40 object-cover rounded-lg"
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      // Disable picture-in-picture
                      const video = e.currentTarget;
                      video.disablePictureInPicture = true;
                    }}
                    onContextMenu={(e) => {
                      // Prevent right-click context menu that can trigger PiP
                      e.preventDefault();
                    }}
                    disablePictureInPicture={true}
                  >
                    <source src={showcaseVideoUrls.chro} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-servify-purple/10 to-servify-purple/5 flex items-center justify-center rounded-lg">
                    <p className="text-servify-dark-grey/50">Loading video...</p>
                  </div>
                )}
              </div>
              <h3 className="font-semibold text-servify-dark-grey mb-2">
                Inside Servify's Culture
              </h3>
              <p className="text-sm text-servify-dark-grey/70">
                Building a Culture of Excellence – Insights from our CHRO,
                Bharat Jain
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modals */}
      {isVideoOpen && currentModule && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
            {/* Modal Header */}
            <div className="bg-servify-purple text-white p-6 relative">
              <h2 className="text-2xl font-bold text-center">
                {currentModule.title}
              </h2>
              <p className="text-white text-center mt-2">
                {currentModule.description}
              </p>

              {/* Close Button */}
              <button
                onClick={() => {
                  setIsVideoOpen(false);
                  setCurrentModule(null);
                }}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Video Content */}
            <div className="p-6 flex flex-col items-center">
              <video
                controls
                className="w-full h-auto max-w-3xl rounded-xl shadow-lg no-seek-video"
                autoPlay
                playsInline
                controlsList="nodownload noplaybackrate"
                onEnded={handleVideoComplete}
                onLoadedMetadata={(e) => {
                  const video = e.currentTarget;
                  video.dataset.lastTime = "0";
                }}
                onSeeking={(e) => {
                  e.preventDefault();
                  const video = e.currentTarget;
                  const lastTime = parseFloat(video.dataset.lastTime || "0");
                  video.currentTime = lastTime;
                }}
                onSeeked={(e) => {
                  e.preventDefault();
                  const video = e.currentTarget;
                  const lastTime = parseFloat(video.dataset.lastTime || "0");
                  video.currentTime = lastTime;
                }}
                onTimeUpdate={(e) => {
                  const video = e.currentTarget;
                  if (!video.seeking) {
                    video.dataset.lastTime = video.currentTime.toString();
                  }
                }}
              >
                <source
                  src={
                    currentModule.mediaFile || currentModule.externalUrl || ""
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>

              {/* Mark as Complete Button */}
              <div className="mt-6 flex gap-4">
                <Button
                  onClick={handleVideoComplete}
                  className="px-6 py-2 bg-servify-purple text-white rounded-lg hover:bg-servify-purple-dark transition-colors"
                  data-testid="button-mark-complete"
                >
                  Mark as Complete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isImageOpen && currentImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => {
            console.log("Image modal state changed:", { isImageOpen: false });
            setIsImageOpen(false);
            setCurrentImage(null);
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
            {/* Modal Header */}
            <div className="bg-servify-purple text-white p-6 relative">
              <h2 className="text-2xl font-bold text-center">
                {currentModule?.title}
              </h2>
              <p className="text-white text-center mt-2">
                {currentModule?.description}
              </p>

              {/* Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsImageOpen(false);
                  setCurrentImage(null);
                }}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Image Content */}
            <div className="p-6 flex flex-col items-center bg-gray-50">
              <div className="max-h-[60vh] overflow-y-auto">
                <img
                  src={currentImage}
                  alt={currentModule?.title}
                  className="max-w-full rounded-xl shadow-lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Mark as Complete Button */}
              <div className="mt-6">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsImageOpen(false);
                    setCurrentImage(null);
                  }}
                  className="px-6 py-2 bg-servify-purple text-white rounded-lg hover:bg-servify-purple-dark transition-colors"
                >
                  Mark as Complete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(() => {
        if (!isMCQOpen || !currentModule || !quizzesData) return null;

        const quiz = quizzesData.quizzes.find(
          (q) => q.moduleId === currentModule.id,
        );
        if (!quiz) return null;

        return (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 sticky top-0 z-10">
                <h2 className="text-2xl font-bold text-center">{quiz.title}</h2>
                <p className="text-white text-center mt-2">
                  {quiz.description}
                </p>
              </div>

              {/* Quiz Content */}
              <div className="p-6">
                <div className="space-y-6">
                  {quiz.questions.map((question: any, qIndex: number) => (
                    <div
                      key={question.id}
                      className="p-4 bg-gray-50 rounded-lg"
                    >
                      <p className="font-semibold text-servify-dark-grey mb-3">
                        {qIndex + 1}. {question.question}
                      </p>
                      <div className="space-y-2">
                        {question.options.map((option: any, oIndex: number) => (
                          <label
                            key={oIndex}
                            className="flex items-center p-3 bg-white rounded-lg cursor-pointer hover:bg-servify-purple/5 transition-colors"
                          >
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value={oIndex}
                              checked={mcqAnswers[question.id] === oIndex}
                              onChange={() => {
                                setMcqAnswers({
                                  ...mcqAnswers,
                                  [question.id]: oIndex,
                                });
                              }}
                              className="mr-3"
                            />
                            <span className="text-servify-dark-grey">
                              {option}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 mt-6">
                  <button
                    onClick={() => {
                      setIsMCQOpen(false);
                      resetMCQAnswers();
                      setCurrentModule(null);
                    }}
                    className="px-6 py-2 border border-servify-white-grey text-servify-dark-grey rounded-lg hover:bg-servify-white-grey transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() =>
                      handleMCQSubmit(currentModule, quizzesData, () => {
                        setIsMCQOpen(false);
                        resetMCQAnswers();
                        setCurrentModule(null);
                      })
                    }
                    className="px-6 py-2 bg-servify-purple text-white rounded-lg hover:bg-servify-purple-dark transition-colors"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Dynamic Quiz Modal */}
      <QuizModal
        isOpen={isQuizOpen}
        onClose={() => {
          setIsQuizOpen(false);
          setCurrentQuiz(null);
        }}
        quiz={currentQuiz}
        isAdmin={false}
        onQuizComplete={handleQuizComplete}
      />

      {/* Showcase Video Modal */}
      {isShowcaseVideoOpen && currentShowcaseVideo && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8 relative">
            {/* Modal Header */}
            <div className="bg-servify-purple text-white p-4 relative rounded-t-2xl">
              <h2 className="text-xl font-bold text-center">
                {currentShowcaseVideoTitle}
              </h2>

              {/* Close Button */}
              <button
                onClick={() => {
                  setIsShowcaseVideoOpen(false);
                  setCurrentShowcaseVideo(null);
                }}
                className="absolute top-3 right-3 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                data-testid="button-close-showcase-video"
              >
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Video Content */}
            <div className="p-4 bg-black rounded-b-2xl">
              <video
                controls
                autoPlay
                preload="auto"
                playsInline
                className="w-full rounded-lg"
                style={{ maxHeight: "calc(90vh - 120px)" }}
                data-testid="showcase-video-player"
                onError={(e) => {
                  // Wrap in try-catch to prevent errors from closing modal
                  try {
                    const video = e.target as HTMLVideoElement;
                    const error = video.error;
                    
                    let errorMessage = 'The video could not be loaded.';
                    let shouldRetry = false;
                    
                    if (error) {
                      switch (error.code) {
                        case MediaError.MEDIA_ERR_ABORTED:
                          errorMessage = 'Video loading was aborted. Please try again.';
                          shouldRetry = true;
                          break;
                        case MediaError.MEDIA_ERR_NETWORK:
                          errorMessage = 'Network error. Please check your connection and try again.';
                          shouldRetry = true;
                          break;
                        case MediaError.MEDIA_ERR_DECODE:
                          errorMessage = 'Video decoding error. The file may be corrupted.';
                          break;
                        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                          errorMessage = 'Video format not supported.';
                          break;
                      }
                    }
                    
                    // If it's a network error, try refreshing the URL (might have expired)
                    if (shouldRetry && currentShowcaseVideo) {
                      // Don't await - do it asynchronously to not block
                      (async () => {
                        try {
                          const videoPaths = [
                            "showcase-videos/servify-product-showcase.mp4",
                            "showcase-videos/ceo-message.mp4",
                            "showcase-videos/chro-message.mp4"
                          ];
                          
                          let videoPath = videoPaths.find(path => 
                            currentShowcaseVideo.includes(path.split('/').pop() || '')
                          ) || videoPaths[0];
                          
                          const { clearCachedUrl, getPublicVideoUrl } = await import("@/lib/storageUrls");
                          clearCachedUrl(videoPath);
                          const freshUrl = await getPublicVideoUrl(videoPath, true);
                          
                          video.src = freshUrl;
                          video.load();
                          
                          toast({
                            title: "Refreshing video",
                            description: "Video URL refreshed. Please try playing again.",
                            variant: "default",
                          });
                        } catch (refreshError) {
                          // Silently fail - don't break modal
                        }
                      })();
                      return;
                    }
                    
                    toast({
                      title: "Video playback error",
                      description: errorMessage,
                      variant: "destructive",
                    });
                  } catch (handlerError) {
                    // Silently fail - don't close modal or break anything
                  }
                }}
                onStalled={(e) => {
                  const video = e.target as HTMLVideoElement;
                  console.warn('Video stalled - buffering:', {
                    currentTime: video.currentTime,
                    buffered: video.buffered.length > 0 ? `${video.buffered.start(0)}-${video.buffered.end(0)}` : 'none',
                    networkState: video.networkState
                  });
                }}
                onWaiting={(e) => {
                  const video = e.target as HTMLVideoElement;
                  console.warn('Video waiting for data - buffering:', {
                    currentTime: video.currentTime,
                    readyState: video.readyState
                  });
                }}
                onSuspend={(e) => {
                  const video = e.target as HTMLVideoElement;
                  console.warn('Video suspended - loading paused:', {
                    currentTime: video.currentTime,
                    networkState: video.networkState
                  });
                }}
                onLoadStart={() => {
                  console.log('Video loading started:', currentShowcaseVideo);
                }}
                onCanPlay={() => {
                  console.log('Video can start playing');
                }}
                onLoadedData={() => {
                  console.log('Video data loaded');
                }}
                onProgress={(e) => {
                  const video = e.target as HTMLVideoElement;
                  if (video.buffered.length > 0) {
                    const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                    const bufferedPercent = (bufferedEnd / video.duration) * 100;
                    // Log if buffering is low
                    if (bufferedPercent < 50 && video.currentTime > 0) {
                      console.warn('Low buffer:', {
                        buffered: `${bufferedPercent.toFixed(1)}%`,
                        currentTime: video.currentTime,
                        duration: video.duration
                      });
                    }
                  }
                }}
              >
                <source src={currentShowcaseVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
